import { useAppSelector } from '@/redux/hooks'
import classnames from 'classnames/bind'
import { isToday as checkIsToday } from 'date-fns'
import { omit } from 'lodash-es'
import { MouseEvent as ReactMouseEvent, ReactNode, useState } from 'react'

// Libs
import useCheckRoomCanAccess from '@/hooks/livemallclub/useCheckRoomCanAccess'
import useVipOnlyPopup from '@/hooks/livemallclub/useVipOnlyPopup'
import { useFavoriteLive } from '@/hooks/useFavoriteLive'
import { enterRoom } from '@/methods/livemallclub/enterLiveRoom'
import { enterLiveRoomDetails } from '@/methods/livemallclub/enterLiveRoomDetails'

// Components
import Image from '@/components/common/Image'
import LivePreviewDrawer from '@/components/shared/livemallclub/LivePreview'
import FollowHeart from '../FollowHeart'
import VideoStatusTag from '../VideoStatusTag'
import VipOnlyMask from '../VipOnlyMask'
import LiveCountdown from './LiveCountDown'
import LiveFeedItemAccordion, { LiveFeedItemAccordionProps } from './LiveFeedItemAccordion'
import LiveFeedItemButtons, { LiveFeedItemButton } from './LiveFeedItemButtons'
import LiveFeedVisibilitySwitch from './LiveFeedVisibilitySwitch'

// Constants
import { VipShopType } from '@/api/fetchMemberVips'
import { ROOM_LIVE_TYPE } from '@/constants/liveRoom'
import { ErrorData } from '@/types'
import { FeedType } from '@/types/feed'

// Styles
import styles from './style.module.scss'

const cx = classnames.bind(styles)

export type ExploreFeedLiveItemProps = FeedType & {
  refetchCurrentPage?: () => void
  isMyLive?: boolean
  useFavoriteState?: boolean
  accordionProps?: LiveFeedItemAccordionProps
  buttons?: LiveFeedItemButton[]
  footer?: ReactNode
  isInvited?: boolean
  vipShopList?: VipShopType[]
  showStartTime?: boolean
}

const renderProductImage = (products: FeedType['roomProducts']) => {
  const shouldShowTwoImagesByOneProduct = products?.length === 1
  const safeProductData = products.filter((product) => product.coverUrl?.[0]?.url).sort((a, b) => a.sort - b.sort)
  if (shouldShowTwoImagesByOneProduct) {
    const hasSecondImage = safeProductData[0].coverUrl?.[1]?.url
    const isAllDataSafe = safeProductData[0].coverUrl?.[0]?.url && hasSecondImage
    return (
      <>
        {isAllDataSafe && (
          <>
            <div className={cx('feed-live-card__content__images-image')}>
              <Image src={safeProductData[0].coverUrl?.[0]?.url} alt={'product-photo'} fill />
            </div>
            {hasSecondImage && (
              <div className={cx('feed-live-card__content__images-image')}>
                <Image src={safeProductData[0].coverUrl?.[1].url} alt={'product-photo'} fill />
              </div>
            )}
          </>
        )}
      </>
    )
  } else {
    return (
      <>
        {safeProductData?.map((productInfo, index) => (
          <div className={cx('feed-live-card__content__images-image')} key={index}>
            <Image src={productInfo.coverUrl?.[0]?.url} alt={'product-photo'} fill />
          </div>
        ))}
      </>
    )
  }
}

export const checkIsVip = ({ vipShopList = [], merchantId }: { vipShopList?: VipShopType[]; merchantId?: number }) => {
  return vipShopList.some((vipShop) => vipShop.merchantId === merchantId)
}

const ExploreFeedLiveItem = (props: ExploreFeedLiveItemProps) => {
  const { meta, roomProducts, refetchCurrentPage, isMyLive = false, useFavoriteState = false, vipShopList = [], showStartTime = false } = props
  const { coverPhotoUrl, liveType, roomId, liveTime, ownerName, ownerAvatarUrl, title, roomDesc, startTime, liveSourceType, isVip: isVipOnly } = meta
  const { memberData } = useAppSelector((state) => state.member)

  const [isFavoriteState, setIsFavoriteState] = useState(meta.isFavorite)
  const { checkRoomCanAccess } = useCheckRoomCanAccess()

  const isLive = liveType === ROOM_LIVE_TYPE.CURRENT && !!liveTime
  const isFuture = liveType === ROOM_LIVE_TYPE.FUTURE
  const isToday = checkIsToday(new Date(startTime))
  const isPast = liveType === ROOM_LIVE_TYPE.PAST
  const isFavorite = useFavoriteState ? isFavoriteState : meta.isFavorite
  const isVip = checkIsVip({ vipShopList, merchantId: Number(meta.ownerMerchantId) })
  const isOwnRoom = memberData?.memberInfoId === meta.ownerMemberInfoId
  const showVipOnlyMask = isVipOnly && !isVip && !isOwnRoom

  const displayStartTime = (() => {
    if (isMyLive) {
      return startTime
    }
    return isLive ? liveTime : startTime
  })()
  const [isLivePreviewOpen, setIsLivePreviewOpen] = useState(false)
  const { PopupComponent: VipOnlyPopup, handleOpen: openVipOnlyPopup } = useVipOnlyPopup()

  const { handleAddFavoriteLive, handleDeleteFavoriteLive, isLoading: isUpdatingFavorite } = useFavoriteLive()

  const handleFavoriteClick = async () => {
    if (isUpdatingFavorite) return

    const newFavoriteState = !isFavorite
    const favoriteAction = newFavoriteState ? handleAddFavoriteLive : handleDeleteFavoriteLive

    try {
      const res = await favoriteAction({ roomId })
      setIsFavoriteState(newFavoriteState)

      if (!res.isSuccess) {
        setIsFavoriteState(!newFavoriteState)
      }
    } catch (error) {
      setIsFavoriteState(!newFavoriteState)
    } finally {
      refetchCurrentPage?.()
    }
  }
  const handleLiveCardClick = async (event: ReactMouseEvent<HTMLDivElement>) => {
    if (isLivePreviewOpen) return

    event.stopPropagation()
    try {
      await checkRoomCanAccess(roomId)
      if (isLive) {
        enterRoom({ roomId, liveType, liveSourceType })
      } else {
        isMyLive ? enterLiveRoomDetails({ id: roomId }) : setIsLivePreviewOpen(true)
      }
    } catch (error) {
      if ((error as ErrorData).status === 403) {
        openVipOnlyPopup()
      }
    }
  }

  return (
    <>
      <div className={cx('feed-live-card', { isLive, isMyLive, isToday })} onClick={handleLiveCardClick}>
        <div className={cx('feed-live-card__content-wrapper', { isMyLive, isToday })}>
          <div className={cx('feed-live-card__future_content')}>
            <div className={cx('feed-live-card__content')}>
              <div className={cx('feed-live-card__content__heading')}>
                <VideoStatusTag
                  roomId={roomId}
                  typeMap={{
                    live: isLive,
                    past: isPast,
                    future: isFuture,
                    myLive: isMyLive,
                    today: isToday,
                    vip: isVipOnly,
                  }}
                  time={displayStartTime}
                  showStartTime={showStartTime}
                />
                {!isMyLive && (
                  <FollowHeart isActive={isFavorite} className={cx('feed-live-card__content__heading-block-follow')} onClick={handleFavoriteClick} />
                )}
              </div>
              <div className={cx('feed-live-card__content__owner')}>
                <div className={cx('feed-live-card__content__owner-logo')}>
                  <Image src={ownerAvatarUrl} fill alt='owner-avatar' />
                </div>
                <div className={cx('feed-live-card__content__owner-name')}>{ownerName}</div>
              </div>
              <p className={cx('feed-live-card__content-title', { isMyLive })}>{title}</p>
            </div>
            {(isMyLive || !isLive) && (
              <div className={cx('feed-live-card__image', { isLive })}>
                <Image width={94} height={94} src={coverPhotoUrl} alt='coverPhoto' style={{ objectFit: 'cover' }} />
                {showVipOnlyMask && <VipOnlyMask />}
              </div>
            )}
          </div>
          <p className={cx('feed-live-card__content-description', { isLive })}>{roomDesc}</p>
          {isLive && roomProducts && <div className={cx('feed-live-card__content__images', { isLive })}>{renderProductImage(roomProducts)}</div>}
          <LivePreviewDrawer
            {...meta}
            onClose={() => setIsLivePreviewOpen(false)}
            isDrawerOpen={isLivePreviewOpen}
            handleHeartClick={handleFavoriteClick}
            isActive={!!meta.isFavorite}
          />
        </div>
        {props.accordionProps && (
          <LiveFeedItemAccordion
            items={props.accordionProps.items.map((item) => ({
              ...item,
              key: roomId,
            }))}
            {...omit(props.accordionProps, 'items')}
          />
        )}
        {props.buttons && (
          <LiveFeedItemButtons
            buttons={props.buttons}
            roomInfo={{ roomId, startTime }}
            style={{
              margin: '0 8px 6px 10px',
            }}
          />
        )}
        {isMyLive && isToday && (
          <LiveCountdown startTime={startTime} liveTime={liveTime} roomId={roomId} liveType={liveType} liveSourceType={liveSourceType} />
        )}
        {props.footer && props.footer}
        {props.canHide && !isLive && !isToday && <LiveFeedVisibilitySwitch initialVisibility={meta.valid} roomId={roomId} />}
        {!isMyLive && isLive && (
          <div className={cx('feed-live-card__image', { isLive })}>
            <Image width={94} height={171} src={coverPhotoUrl} alt='coverPhoto' style={{ objectFit: 'cover', height: '100%' }} />
          </div>
        )}
      </div>
      <VipOnlyPopup />
    </>
  )
}
export default ExploreFeedLiveItem
